import React from "react";
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import {faSun} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {format} from "date-fns";
import {ChartNcu} from "../../../../Shared/Types/ChartNcu";
import {Tracker} from "../../../../Shared/Types/Tracker";
import {ChartSeriesType} from "../../../../Shared/Types/chartSeriesType";
import {COLORS} from "../../../../Utils/Colors";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {TrackerPositionPoint, TrackerPositionSeries} from "./TrackerPositionHeatmap";

export const parsePositionValue = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "null" || value === "") {
        return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

export const hasFiniteTrackerPoint = (chart?: ChartNcu | null) => chart?.series?.some((series: any) => (
    (series.data ?? series.dataByTime ?? []).some((point: [string, unknown]) => (
        parsePositionValue(point?.[1]) !== null
    ))
)) ?? false;

const toTrackerPositionPoints = (source: unknown): TrackerPositionPoint[] | undefined => {
    if (!Array.isArray(source)) {
        return undefined;
    }

    return source.reduce<TrackerPositionPoint[]>((points, point) => {
        if (!Array.isArray(point) || point.length < 2) {
            return points;
        }
        const timestamp = point[0];
        if (!(timestamp instanceof Date) &&
            typeof timestamp !== "string" &&
            typeof timestamp !== "number") {
            return points;
        }
        points.push([timestamp, point[1]]);
        return points;
    }, []);
};

export const toTrackerPositionSeries = (chart?: ChartNcu | null): TrackerPositionSeries[] => (
    (chart?.series ?? []).map(series => {
        const runtimeSeries = series as unknown as {
            name?: string | null;
            data?: unknown;
            dataByTime?: unknown;
        };
        return {
            name: runtimeSeries.name,
            data: toTrackerPositionPoints(runtimeSeries.data),
            dataByTime: toTrackerPositionPoints(runtimeSeries.dataByTime)
        };
    })
);

const calculateValueMax = (value: number) => {
    if(value === 0){
        return 1;
    } else if(value > 0){
        return truncateNumber(value + (value * 15)/100);
    }
    return truncateNumber(value - (value * 15)/100);
}

export function createOptions(chart: ChartNcu | null){
    let nameList: string[] = [];
    chart?.series && chart.series.forEach((s: ChartSeriesType) => {
        if (s.name != null) {
            nameList.push(s.name);
        }
    });

    let series = chart?.series ? chart.series.map((serie: any) => ({
        ...serie,
        showSymbol: false,
        connectNulls: false,
        data: (serie.data ?? serie.dataByTime ?? []).map(([time, value]: [string, unknown]) => [
            time,
            parsePositionValue(value)
        ])
    })) : [];
    let windowSize = window.innerWidth;
    const colors = series.map((_, i) => COLORS[i % COLORS.length]);
    let globalMaxValue = -Infinity;
    let globalMinValue = Infinity;
    series.forEach(serie => {
        if(serie.data) {
            const validData = serie.data
                .map((element: [string, unknown]) => parsePositionValue(element[1]))
                .filter((value: number | null): value is number => value !== null);

            if (validData.length > 0) {
                const serieMaxValue = Math.max(...validData);
                const serieMinValue = Math.min(...validData);
                globalMaxValue = Math.max(globalMaxValue, serieMaxValue);
                globalMinValue = Math.min(globalMinValue, serieMinValue);
            }
        }
    });

    const hasValidRange = Number.isFinite(globalMaxValue) && Number.isFinite(globalMinValue);

    // @ts-ignore
    const options: AChartXProps['option'] = {
        legend: {
            type: 'scroll',
            orient: windowSize < 1050 ? 'horizontal' : 'vertical',
            right: 4,
            top: 25,
            bottom: 0,
            height: 375,
            data: nameList
        },

        xAxis: chart?.xAxis ? chart?.xAxis[0] : {},
        yAxis: chart?.yAxis ? {
            ...chart?.yAxis[0],
            max: hasValidRange && globalMaxValue < 180
                ? truncateNumber(calculateValueMax(globalMaxValue))
                : (chart.yAxis[0].max ?? 180),
            min: hasValidRange && globalMinValue > -180
                ? truncateNumber(globalMinValue - Math.abs(globalMinValue * 15) / 100)
                : (chart.yAxis[0].min ?? -180),
            interval: truncateNumber(180 / 7)
        } : {},
        series: series,
        color: colors,
        tooltip: {
            trigger: "axis",
            triggerOn: "mousemove",
            appendToBody: true,
            enterable: true,
            confine: true,
            alwaysShowContent: false,
            className: 'custom-tooltip',
            position: function (point: any) {
                return [point[0] - 160, point[1]];
            },
            formatter: function (params: any) {
                let formatDate = "HH:mm";
                let dateStr = format(new Date(params[0].axisValue), formatDate);

                let content = `
                    <table style="border-collapse: collapse; display: inline-block; margin-right: 10px;">
                        <tr>
                            <th style="padding: 5px; text-align: left;">Série</th>
                            <th style="padding: 5px; text-align: right;">Valor</th>
                        </tr>
                        ${params
                    .map(
                        (item: any) => `
                        <tr>
                            <td style="padding: 5px; text-align: left;">${item.marker} ${item.seriesName}</td>
                            <td style="padding: 5px; text-align: right;"><b>${item.value[1]}</b></td>
                        </tr>`
                    )
                    .join("")}
                    </table>`

                return `
                    <div  style="pointer-events: all; padding: 2px; text-align: right;">${dateStr}</div>
                    <div style="padding: 5px;
                                border: 1px solid #ddd;
                                max-height: 250px;
                                overflow-y: auto;
                                white-space: nowrap;
                                display: block;
                                pointer-events: all;
                                ">
                        ${content}
                    </div>`;
            },
            backgroundColor: "rgb(255,255,255)",
            textStyle: { color: "#000" },
            borderWidth: 0,
        },
        toolbox: {
            right: '6%',
            itemSize: 25,
            top: 40,
            feature: {
                dataZoom: {
                    yAxisIndex: 'none'
                },
                restore: {}
            }
        },
        dataZoom: [
            {
                type: 'inside',
                throttle: 50
            },
        ],
    }
    return options;
}

export function createOptionsSpeed(ncu: any) {
    // @ts-ignore
    const optionSpeed: AChartXProps['option'] = {
        series: [
            {
                type: 'gauge',
                min: 0,
                max: 240,
                axisLine: {
                    lineStyle: {
                        width: 10,
                        color: [
                            [0.3, '#67e0e3'],
                            [0.7, '#37a2da'],
                            [1, '#fd666d']
                        ]
                    }
                },
                pointer: {
                    itemStyle: {
                        color: 'auto'
                    }
                },
                axisTick: {
                    distance: -10,
                    length: 8,
                    lineStyle: {
                        color: '#fff',
                        width: 2
                    }
                },
                splitLine: {
                    distance: -45,
                    length: 20,
                    lineStyle: {
                        color: '#fff',
                        width: 2
                    }
                },
                axisLabel: {
                    color: 'inherit',
                    distance: 40,
                    fontSize: 8
                },
                detail: {
                    valueAnimation: true,
                    formatter: '{value} km/h',
                    color: 'inherit',
                    fontSize: 10
                },
                data: [
                    {
                        value: ncu.windSpeed
                    }
                ]
            }
        ]
    };

    return optionSpeed;
}

export function createOptionDirection(ncu: any) {
    // @ts-ignore
    const optionDirection: AChartXProps['option'] = {
        grid: {
            top: -25,
        },
        toolbox: {
            top: -10,
        },
        tooltip:{
            trigger: 'item',
            borderWidth: 0,
            renderMode: 'richText',
        },
        series: [
            {
                name: 'Direção do Vento',
                type: 'gauge',
                startAngle: 90,
                endAngle: -270,
                min: 0,
                max: 360,
                detail: {
                    valueAnimation: true,
                    formatter: '{value} °',
                    fontSize: 10
                },
                axisLabel: {
                    show: false
                },
                axisTick: {
                    distance: 0,
                    length: 5,
                    lineStyle: {
                        color: '#bcbcbc',
                        width: 2
                    }
                },
                axisLine: {
                    fontSize: 10
                },
                splitLine: {
                    distance: 5,
                    length: 5,
                    lineStyle: {
                        color: 'black',
                        width: 2
                    }
                },
                title: {
                    fontSize: 8
                },
                pointer: {
                    icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
                    length: '15%',
                    width: 10,
                    offsetCenter: [0, '-40%'],
                    itemStyle: {
                        color: 'blue'
                    }
                },
                data: [
                    { value: ncu.windDirection }
                ]
            }
        ]
    };

    return optionDirection;
}

const headTitle = ['Tracker', 'Posição Alvo', 'Posição do Tracker'];

export function createTable(trackers: Tracker[]){
    let numTabelas = 1;
    let tabelas = [[]];

    if(window.innerWidth > 770 && window.innerWidth < 1400) {
        tabelas.push([]);
        numTabelas = 2;
    }
    if(window.innerWidth > 1400) {
        tabelas.push([]);
        tabelas.push([]);
        numTabelas = 3;
    }

    for (let i = 0; i < trackers?.length; i++) {
        const grupoIndex = i % numTabelas;
        // @ts-ignore
        tabelas[grupoIndex].push(trackers[i]);
    }

    return (
        <div className="trackersTable">
            {
                tabelas && tabelas.length > 0 && tabelas.map((tabela: any, index: number) => {
                    return (
                        <TableContainer key={index.valueOf()}>
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        {headTitle.map((title) => {
                                            return (
                                                <TableCell
                                                    key={title}
                                                    align={"center"}
                                                    style={{
                                                        backgroundColor: "rgba(4,104,190,0.92)",
                                                        color: "white"
                                                    }}
                                                >
                                                    {title}
                                                </TableCell>
                                            )
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tabelas && tabelas.length > 0 && tabela.map((row: any) => (
                                        <TableRow
                                            key={row.tracker}
                                            sx={{'&:last-child td, &:last-child th': {border: 0},
                                                backgroundColor: row.alarmed ? '#F85F4E' : 'inherit' // Vermelho claro se estiver alarmado
                                            }}
                                        >
                                            <TableCell
                                                align="left"
                                                component="th"
                                                scope="row"
                                                sx={{display: 'flex', justifyContent: 'space-between'}}
                                            >
                                                <FontAwesomeIcon icon={faSun} style={{
                                                    color: 'gray'
                                                }}/>
                                                {row.name}
                                            </TableCell>
                                            <TableCell
                                                align="center">{row.lastSunPosition == null ? '-' : row.lastSunPosition + '°'}</TableCell>
                                            <TableCell
                                                align="center">{row.lastTrackerPosition == null ? '-' : row.lastTrackerPosition + '°'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                })
            }
        </div>
    )
}
